/**
 * Secondary experience: Company explorer with filters and grid
 */

import React, { useState, useMemo } from 'react'
import { Search, ChevronLeft, ChevronRight } from 'lucide-react'
import type { Company } from './types'
import { CompanyCard } from './CompanyCard'
import { CompanyDetail } from './CompanyDetail'
import { DomainPills } from './DomainPills'

interface CompanyExplorerProps {
  companies: Company[]
  domains: { domain: string; count: number }[]
  selectedDomain: string | null
  onDomainSelect: (domain: string | null) => void
  searchTerm: string
  onSearchChange: (term: string) => void
}

const ITEMS_PER_PAGE = 24

export const CompanyExplorer: React.FC<CompanyExplorerProps> = ({
  companies,
  domains,
  selectedDomain,
  onDomainSelect,
  searchTerm,
  onSearchChange,
}) => {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [selectedDomain, searchTerm])

  // Paginate
  const totalPages = Math.ceil(companies.length / ITEMS_PER_PAGE)
  const paginatedCompanies = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return companies.slice(start, start + ITEMS_PER_PAGE)
  }, [companies, currentPage])

  return (
    <div className="space-y-6">
      {/* Header with search */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <h2 className="text-xl font-bold text-primary">
          Explore Companies
          <span className="text-secondary font-normal ml-2">
            ({companies.length} companies)
          </span>
        </h2>

        {/* Search */}
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-secondary border border-tertiary rounded-lg text-primary placeholder:text-secondary focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>
      </div>

      {/* Domain pills */}
      <DomainPills
        domains={domains}
        selectedDomain={selectedDomain}
        onSelect={onDomainSelect}
        totalCount={companies.length}
      />

      {/* Company grid */}
      {paginatedCompanies.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedCompanies.map((company) => (
            <CompanyCard
              key={company.id || company.slug}
              company={company}
              onClick={() => setSelectedCompany(company)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-secondary">
          No companies match your filters
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg bg-secondary hover:bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (currentPage <= 3) {
                pageNum = i + 1
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = currentPage - 2 + i
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-accent text-white'
                      : 'bg-secondary hover:bg-tertiary text-primary'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg bg-secondary hover:bg-tertiary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Company detail modal */}
      {selectedCompany && (
        <CompanyDetail
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}
    </div>
  )
}

export default CompanyExplorer
